import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  IconButton,
  Button,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import { getUserPermissions } from "src/utils/permissionsUtils";
import { convertUTCtoET } from "src/utils/timeUtils";

// Style constants for consistency
const STYLES = {
  textField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#ffffff",
      "&:hover": {
        backgroundColor: "#f8f9fa",
      },
      "&.Mui-focused": {
        backgroundColor: "#ffffff",
      },
    },
    "& .MuiInputBase-input": {
      padding: "8px 12px",
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.875rem",
    },
    "& .MuiFormHelperText-root": {
      fontSize: "0.7rem",
      marginTop: "2px",
    },
  },
  sectionHeader: {
    fontWeight: 600,
    fontSize: "0.875rem",
    color: "text.primary",
    mb: 0.5,
  },
  paper: {
    p: 2,
    borderRadius: 2,
    border: "1px solid #e0e0e0",
    transition: "box-shadow 0.3s ease-in-out",
    "&:hover": {
      boxShadow: 3,
    },
  },
};

// Helper component for mission field
const MissionField = ({ label, value, onChange, type = "text", disabled, min = 0, helperText, ...props }) => (
  <TextField
    fullWidth
    label={label}
    type={type}
    value={value}
    onChange={onChange}
    variant="outlined"
    disabled={disabled}
    helperText={helperText}
    inputProps={type === "number" ? { min, step: 1 } : undefined}
    sx={STYLES.textField}
    {...props}
  />
);

// Helper component for mission card
const MissionCard = ({ mission, index, onFieldChange, onDelete, disabled, canDelete }) => (
  <Paper elevation={1} sx={STYLES.paper}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
      <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 600 }}>
        {mission.name || `Mission ${index + 1}`}
      </Typography>
      <Tooltip title={canDelete ? "Delete mission" : "At least one mission is required"}>
        <span>
          <IconButton
            onClick={onDelete}
            disabled={disabled || !canDelete}
            size="small"
            color="error"
            sx={{
              opacity: canDelete ? 1 : 0.3,
              p: 0.5,
              "&:hover": {
                backgroundColor: canDelete ? "#ffcdd2" : "transparent",
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>

    <Grid container spacing={1.5}>
      {/* Basic Information */}
      <Grid item xs={12} sm={2}>
        <MissionField
          label="Mission Name"
          value={mission.name}
          onChange={(e) => onFieldChange("name", e.target.value)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={1}>
        <MissionField
          label="Entry Cost"
          type="number"
          value={mission.entry}
          onChange={(e) => onFieldChange("entry", e.target.value)}
          disabled={disabled}
        />
      </Grid>

      {/* Game Settings */}
      <Grid item xs={12} sm={1}>
        <FormControl fullWidth variant="outlined" disabled={disabled} sx={STYLES.textField}>
          <InputLabel sx={{ fontSize: "0.875rem" }}>Mission Type</InputLabel>
          <Select
            value={mission.missionType}
            onChange={(e) => onFieldChange("missionType", e.target.value)}
            label="Mission Type"
            sx={{
              "& .MuiSelect-select": {
                padding: "8px 12px",
              },
            }}
          >
            <MenuItem value="green">Green</MenuItem>
            <MenuItem value="red">Red</MenuItem>
            <MenuItem value="blue">Blue</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={1}>
        <FormControl fullWidth variant="outlined" disabled={disabled} sx={STYLES.textField}>
          <InputLabel sx={{ fontSize: "0.875rem" }}>Grid Size</InputLabel>
          <Select
            value={mission.gridSizeType || "8x8"}
            onChange={(e) => onFieldChange("gridSizeType", e.target.value)}
            label="Grid Size"
            sx={{
              "& .MuiSelect-select": {
                padding: "8px 12px",
              },
            }}
          >
            <MenuItem value="8x8">8x8</MenuItem>
            <MenuItem value="8x4">8x4</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={1} md={1}>
        <MissionField
          label="Pairs"
          type="number"
          value={mission.pairsRepeat}
          onChange={(e) => onFieldChange("pairsRepeat", e.target.value)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={1}>
        <MissionField
          label="Max Moves"
          type="number"
          value={mission.maxMove}
          onChange={(e) => onFieldChange("maxMove", e.target.value)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={1} md={1}>
        <MissionField
          label="Max Time (sec)"
          type="number"
          value={mission.maxTime}
          onChange={(e) => onFieldChange("maxTime", e.target.value)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={1} md={1}>
        <MissionField
          label="Commission %"
          type="number"
          value={mission.commission}
          onChange={(e) => onFieldChange("commission", e.target.value)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={1} md={1}>
        <MissionField
          label="Jackpot Start"
          type="number"
          value={mission.jackpotLimit}
          onChange={(e) => onFieldChange("jackpotLimit", e.target.value)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={2} md={2}>
        <MissionField
          label="Prize Breakdown"
          value={mission.prizeBreakdown}
          onChange={(e) => onFieldChange("prizeBreakdown", e.target.value)}
          disabled={disabled}
          placeholder="e.g., 1=65,2=25,3=15"
        />
      </Grid>
    </Grid>
  </Paper>
);

export default function MastersMissionsConfig({
  gameConfig,
  setGameConfig,
  loading,
}) {
  const { isViewer } = getUserPermissions();

  // Get current configurations with fallback
  const getCurrentConfigs = useMemo(() => {
    if (gameConfig.config && gameConfig.config.length > 0) {
      // Ensure missionType is always set on existing missions
      return gameConfig.config.map(timeSlot => ({
        ...timeSlot,
        missions: timeSlot.missions.map(mission => ({
          ...mission,
          missionType: mission.missionType || "green"
        }))
      }));
    }
    return [
      {
        id: 1,
        startTime: "00:00",
        endTime: "23:59",
        missions: [
          {
            id: 1,
            name: "Mission 1",
            missionType: "green",
            entry: 10,
            commission: 10,
            jackpotLimit: 1000,
            maxMove: 30,
            maxTime: 300,
            pairsRepeat: 1,
            prizeBreakdown: "1=65,2=25,3=15",
            gridSizeType: "8x8",
          },
        ],
      },
    ];
  }, [gameConfig.config]);

  const handleTimeSlotChange = (timeSlotIndex, field, value) => {
    const updatedConfigs = [...getCurrentConfigs];
    updatedConfigs[timeSlotIndex] = {
      ...updatedConfigs[timeSlotIndex],
      [field]: value,
    };
    setGameConfig({ ...gameConfig, config: updatedConfigs });
  };

  const handleMissionChange = (timeSlotIndex, missionIndex, field, value) => {
    const updatedConfigs = [...getCurrentConfigs];
    const updatedMission = {
      ...updatedConfigs[timeSlotIndex].missions[missionIndex],
      [field]: field === "name" || field === "prizeBreakdown" || field === "gridSizeType" || field === "missionType" ? value : parseFloat(value) || 0,
    };
    updatedConfigs[timeSlotIndex].missions[missionIndex] = updatedMission;

    setGameConfig({ ...gameConfig, config: updatedConfigs });
  };

  const handleAddMission = (timeSlotIndex) => {
    const updatedConfigs = [...getCurrentConfigs];
    const currentMissions = updatedConfigs[timeSlotIndex].missions;
    const maxId = Math.max(...currentMissions.map((m) => m.id), 0);

    const newMission = {
      id: maxId + 1,
      name: `Mission ${currentMissions.length + 1}`,
      missionType: "green",
      entry: 10,
      commission: 10,
      jackpotLimit: 1000,
      maxMove: 30,
      maxTime: 300,
      prizeBreakdown: "1=65,2=25,3=15",
      gridSizeType: "8x8",
    };

    updatedConfigs[timeSlotIndex].missions = [...currentMissions, newMission];
    setGameConfig({ ...gameConfig, config: updatedConfigs });
  };

  const handleDeleteMission = (timeSlotIndex, missionIndex) => {
    const updatedConfigs = [...getCurrentConfigs];
    if (updatedConfigs[timeSlotIndex].missions.length <= 1) return;

    updatedConfigs[timeSlotIndex].missions = updatedConfigs[timeSlotIndex].missions.filter(
      (_, i) => i !== missionIndex
    );

    setGameConfig({ ...gameConfig, config: updatedConfigs });
  };

  const handleDeleteTimeSlot = (timeSlotIndex) => {
    const updatedConfigs = [...getCurrentConfigs];
    if (updatedConfigs.length <= 1) return;

    const filteredConfigs = updatedConfigs.filter((_, i) => i !== timeSlotIndex);
    setGameConfig({ ...gameConfig, config: filteredConfigs });
  };

  const handleAddTimeSlot = () => {
    const updatedConfigs = [...getCurrentConfigs];
    const maxId = Math.max(...updatedConfigs.map((ts) => ts.id), 0);

    const newTimeSlot = {
      id: maxId + 1,
      startTime: "00:00",
      endTime: "23:59",
      missions: [
        {
          id: 1,
          name: "Mission 1",
          entry: 10,
          commission: 10,
          jackpotLimit: 1000,
          maxMove: 30,
          maxTime: 300,
          prizeBreakdown: "1=65,2=25,3=15",
          gridSizeType: "8x8",
        },
      ],
    };

    setGameConfig({ ...gameConfig, config: [...updatedConfigs, newTimeSlot] });
  };

  return (
    <Box sx={{ mt: 1 }}>
      {/* Header Section */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          Missions Configuration
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Configure missions for different time slots with difficulty levels and prize structures.
        </Typography>
      </Box>

      {/* Time Slots */}
      <Stack spacing={2}>
        {getCurrentConfigs.map((timeSlot, timeSlotIndex) => (
          <Accordion
            key={`timeslot-${timeSlot.id}`}
            defaultExpanded
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px !important",
              "&:before": { display: "none" },
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: "#f8f9fa",
                borderRadius: "8px 8px 0 0",
                minHeight: 48,
                "& .MuiAccordionSummary-content": {
                  margin: "8px 0",
                },
                "&:hover": {
                  backgroundColor: "#e9ecef",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                <TimeIcon color="primary" fontSize="small" />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {timeSlot.startTime} - {timeSlot.endTime}
                  </Typography>
                </Box>
                <Chip
                  label={timeSlot.missions?.length || 0}
                  color="primary"
                  size="small"
                  variant="outlined"
                />
                {!isViewer && getCurrentConfigs.length > 1 && (
                  <Tooltip title="Delete time slot">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTimeSlot(timeSlotIndex);
                      }}
                      disabled={loading}
                      size="small"
                      color="error"
                      sx={{
                        p: 0.5,
                        "&:hover": {
                          backgroundColor: "#ffcdd2",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 2, backgroundColor: "#fafafa" }}>
              <Stack spacing={2}>
                {/* Time Slot Configuration */}
                <Paper elevation={0} sx={{ p: 2, backgroundColor: "#fff", border: "1px solid #e0e0e0" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Time Slot Settings
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Start Time (UTC)"
                        type="time"
                        value={timeSlot.startTime}
                        onChange={(e) => handleTimeSlotChange(timeSlotIndex, "startTime", e.target.value)}
                        disabled={loading || isViewer}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                        sx={STYLES.textField}
                      />
                      {timeSlot.startTime && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.5,
                            color: 'text.secondary',
                            fontStyle: 'italic'
                          }}
                        >
                          ET: {convertUTCtoET(timeSlot.startTime)}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="End Time (UTC)"
                        type="time"
                        value={timeSlot.endTime}
                        onChange={(e) => handleTimeSlotChange(timeSlotIndex, "endTime", e.target.value)}
                        disabled={loading || isViewer}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                        sx={STYLES.textField}
                      />
                      {timeSlot.endTime && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.5,
                            color: 'text.secondary',
                            fontStyle: 'italic'
                          }}
                        >
                          ET: {convertUTCtoET(timeSlot.endTime)}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Paper>

                {/* Missions List */}
                {timeSlot.missions?.map((mission, missionIndex) => (
                  <MissionCard
                    key={`mission-${mission.id}`}
                    mission={mission}
                    index={missionIndex}
                    onFieldChange={(field, value) =>
                      handleMissionChange(timeSlotIndex, missionIndex, field, value)
                    }
                    onDelete={() => handleDeleteMission(timeSlotIndex, missionIndex)}
                    disabled={loading || isViewer}
                    canDelete={timeSlot.missions.length > 1}
                  />
                ))}

                {/* Add Mission Button */}
                {!isViewer && (
                  <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddMission(timeSlotIndex)}
                      disabled={loading}
                      sx={{
                        borderStyle: "dashed",
                        borderWidth: 2,
                        py: 0.5,
                        px: 2,
                        "&:hover": {
                          borderWidth: 2,
                          borderStyle: "dashed",
                          backgroundColor: "rgba(25, 118, 210, 0.04)",
                        },
                      }}
                    >
                      Add Mission
                    </Button>
                  </Box>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      {/* Add Time Slot Button */}
      {!isViewer && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<AddIcon />}
            onClick={handleAddTimeSlot}
            disabled={loading}
            sx={{
              borderStyle: "dashed",
              borderWidth: 2,
              py: 1,
              px: 3,
              "&:hover": {
                borderWidth: 2,
                borderStyle: "dashed",
                backgroundColor: "rgba(25, 118, 210, 0.04)",
              },
            }}
          >
            Add Time Slot
          </Button>
        </Box>
      )}

      {/* Summary Info */}
      <Paper
        sx={{
          mt: 2,
          p: 1.5,
          backgroundColor: "#e3f2fd",
          border: "1px solid #90caf9",
        }}
      >
        <Typography variant="caption" color="primary.dark">
          <strong>Total:</strong> {getCurrentConfigs.reduce((acc, ts) => acc + (ts.missions?.length || 0), 0)} missions in {getCurrentConfigs.length} time slot(s)
        </Typography>
      </Paper>
    </Box>
  );
}
